import { on } from '@ngrx/store';
import { Answers } from '../../../types/answers';
import { FormFilters, FormFiltersType } from '../../../types/form-filter';
import { FormTypeFilters } from '../../../types/form-type-filters';
import { FormsConstants } from '../../../types/forms-constants';
import { TypeFilter } from '../../../types/type-filter';
import { AnswerUtilities } from '../../../utilities/answer-utilities';
import { clearFilters, updateFormFilter, updateTypeFilter } from '../../actions/form.actions';
import { FormState } from '../../state/form.state';

export function updatedTypeFilterReducer(
    state: Readonly<FormState>,
    props: { elementType: FormsConstants.ELEMENT_TYPE; visible: boolean },
): Readonly<FormState> {
    const oldTypeFilter = state.typeFilters[props.elementType];
    if (typeof oldTypeFilter === 'undefined') {
        throw new Error(
            `FormReducer: updatedTypeFilterReducer: Could not find filter with type '${props.elementType}' in type filters`,
        );
    }

    const newTypeFilter: TypeFilter = { ...state.typeFilters[oldTypeFilter.elementType], visible: props.visible };
    const newTypeFilters: FormTypeFilters = { ...state.typeFilters, [oldTypeFilter.elementType]: newTypeFilter };

    const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
        state.selectedParticipantCopyId,
        state.content,
        state.checkpoints,
        state.participantCopies[state.selectedParticipantCopyId].options,
        state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        state.checkpointDependencies,
        newTypeFilters,
        state.formFilters,
        state.enableManualIndividualization,
        state.type,
    );

    return {
        ...state,
        typeFilters: newTypeFilters,
        participantCopies: {
            ...state.participantCopies,
            [state.selectedParticipantCopyId]: {
                ...state.participantCopies[state.selectedParticipantCopyId],
                answers: newAnswers,
            },
        },
    };
}

export function updateFormFilterReducer(
    state: Readonly<FormState>,
    props: { filterType: FormFiltersType; visible: boolean },
): Readonly<FormState> {
    const oldFormFilter = state.formFilters[props.filterType];
    if (typeof oldFormFilter === 'undefined') {
        throw new Error(
            `FormReducer: updateFormFilterReducer: Could not find filter with type '${props.filterType}' in form filters`,
        );
    }

    const newFormFilters: FormFilters = {
        ...state.formFilters,
        [props.filterType]: { ...state.formFilters[props.filterType], visible: props.visible },
    };

    const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
        state.selectedParticipantCopyId,
        state.content,
        state.checkpoints,
        state.participantCopies[state.selectedParticipantCopyId].options,
        state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        state.checkpointDependencies,
        state.typeFilters,
        newFormFilters,
        state.enableManualIndividualization,
        state.type,
    );

    return {
        ...state,
        formFilters: newFormFilters,
        participantCopies: {
            ...state.participantCopies,
            [state.selectedParticipantCopyId]: {
                ...state.participantCopies[state.selectedParticipantCopyId],
                answers: newAnswers,
            },
        },
    };
}

// reset typeFilters and formFilters
export function clearFiltersReducer(state: Readonly<FormState>): Readonly<FormState> {
    const newTypeFilters: FormTypeFilters = {
        [FormsConstants.ELEMENT_TYPE.A]: { ...state.typeFilters[FormsConstants.ELEMENT_TYPE.A], visible: false },
        [FormsConstants.ELEMENT_TYPE.B]: { ...state.typeFilters[FormsConstants.ELEMENT_TYPE.B], visible: false },
        [FormsConstants.ELEMENT_TYPE.C]: { ...state.typeFilters[FormsConstants.ELEMENT_TYPE.C], visible: false },
        [FormsConstants.ELEMENT_TYPE.D]: { ...state.typeFilters[FormsConstants.ELEMENT_TYPE.D], visible: false },
        [FormsConstants.ELEMENT_TYPE.E]: { ...state.typeFilters[FormsConstants.ELEMENT_TYPE.E], visible: false },
        [FormsConstants.ELEMENT_TYPE.F]: { ...state.typeFilters[FormsConstants.ELEMENT_TYPE.F], visible: false },
        [FormsConstants.ELEMENT_TYPE.STANDARD]: {
            ...state.typeFilters[FormsConstants.ELEMENT_TYPE.STANDARD],
            visible: false,
        },
        [FormsConstants.ELEMENT_TYPE.QUALITY]: {
            ...state.typeFilters[FormsConstants.ELEMENT_TYPE.QUALITY],
            visible: false,
        },
        [FormsConstants.ELEMENT_TYPE.SOFTWARE]: {
            ...state.typeFilters[FormsConstants.ELEMENT_TYPE.SOFTWARE],
            visible: false,
        },
        [FormsConstants.ELEMENT_TYPE.SAFETY]: {
            ...state.typeFilters[FormsConstants.ELEMENT_TYPE.SAFETY],
            visible: false,
        },
        [FormsConstants.ELEMENT_TYPE.TIME]: {
            ...state.typeFilters[FormsConstants.ELEMENT_TYPE.TIME],
            visible: false,
        },
        [FormsConstants.ELEMENT_TYPE.MATERIAL]: {
            ...state.typeFilters[FormsConstants.ELEMENT_TYPE.MATERIAL],
            visible: false,
        },
        [FormsConstants.ELEMENT_TYPE.UNKNOWN]: {
            ...state.typeFilters[FormsConstants.ELEMENT_TYPE.UNKNOWN],
            visible: false,
        },
    };

    const newFormFilters: FormFilters = {
        [FormFiltersType.INCOMPLETE_ELEMENTS]: {
            ...state.formFilters[FormFiltersType.INCOMPLETE_ELEMENTS],
            visible: false,
        },
        [FormFiltersType.NOTOK_ELEMENTS]: {
            ...state.formFilters[FormFiltersType.NOTOK_ELEMENTS],
            visible: false,
        },
    };

    const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
        state.selectedParticipantCopyId,
        state.content,
        state.checkpoints,
        state.participantCopies[state.selectedParticipantCopyId].options,
        state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        state.checkpointDependencies,
        newTypeFilters,
        newFormFilters,
        state.enableManualIndividualization,
        state.type,
    );

    return {
        ...state,
        typeFilters: newTypeFilters,
        formFilters: newFormFilters,
        participantCopies: {
            ...state.participantCopies,
            [state.selectedParticipantCopyId]: {
                ...state.participantCopies[state.selectedParticipantCopyId],
                answers: newAnswers,
            },
        },
    };
}

export const filterReducer = [
    on<FormState, Array<typeof updateTypeFilter>>(updateTypeFilter, updatedTypeFilterReducer),
    on<FormState, Array<typeof updateFormFilter>>(updateFormFilter, updateFormFilterReducer),
    on<FormState, Array<typeof clearFilters>>(clearFilters, clearFiltersReducer),
];
